import React from 'react';
import { Box, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { contentColumn, eyebrow, mono, pageSurface } from '../../../styles/visualLanguage';
import { AdvisoryLeaveButton } from './AdvisoryLeaveButton';

/**
 * AdvisoryEngagementShell
 *
 * The shared chrome: top bar (position/counter, leave affordance),
 * eyebrow/title/hint slots, a body slot, and an action row — every advisory
 * screen composes this rather than re-deciding its own chrome. It owns no
 * data fetching, no stage derivation and no listener; it is chrome plus
 * slots, and it stays separately importable so a step body can be rendered
 * into it with zero knowledge of how the chrome is built.
 *
 * The top bar carries only the step counter and a leave affordance
 * (`AdvisoryLeaveButton` — shared with the run and thinking panels rather
 * than inline chrome here, since three surfaces need the same one) —
 * deliberately no engagement-owner-identity slot (the prototype's own top
 * bar carries a demo-flavour identity label with no analogue here). Leave
 * is worded plainly ("Leave"), never warning about losing unsaved work,
 * because progress genuinely persists server-side.
 *
 * Deliberate divergence from the prototype: the scene below uses the
 * shared 560px `contentColumn` rather than the prototype's own
 * `.wiz-scene` `min(720px, 100%)`, because both advisory routes already
 * share one width primitive and a second one would reintroduce exactly the
 * per-screen width decisions a shared primitive exists to prevent.
 */
export interface AdvisoryEngagementShellProps {
  position: string;
  total: string | null;
  eyebrowKey?: string;
  titleKey?: string;
  hintKey?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}

export function AdvisoryEngagementShell({
  position,
  total,
  eyebrowKey,
  titleKey,
  hintKey,
  children,
  actions,
}: AdvisoryEngagementShellProps) {
  const { t } = useUntypedTranslation();

  const hasTotal = typeof total === 'string';
  const counterText = hasTotal
    ? t('advisory:shell.stepOf', { position, total })
    : t('advisory:shell.stepPosition', { position });
  const counterAriaLabel = hasTotal
    ? t('advisory:shell.stepAriaLabel', { position, total })
    : t('advisory:shell.stepAriaLabelNoTotal', { position });

  return (
    <Box sx={[pageSurface, { display: 'flex', flexDirection: 'column', p: 2.5 }]}>
      <Box
        sx={[
          contentColumn,
          theme => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.visualPrimitives.wizTopGap,
            paddingTop: theme.visualPrimitives.wizTopPaddingY,
            paddingBottom: theme.visualPrimitives.wizTopPaddingY,
          }),
        ]}
      >
        <Typography
          component="span"
          data-testid="advisory-step-counter"
          aria-label={counterAriaLabel}
          sx={[
            mono,
            theme => ({
              fontSize: theme.visualPrimitives.stepSize,
              color: theme.colors.inkTertiary,
              whiteSpace: 'nowrap',
            }),
          ]}
        >
          {counterText}
        </Typography>
        <AdvisoryLeaveButton />
      </Box>

      <Box
        sx={[
          contentColumn,
          theme => ({
            paddingTop: theme.visualPrimitives.wizSceneGapTop,
            paddingBottom: theme.visualPrimitives.wizSceneGapBottom,
          }),
        ]}
      >
        {eyebrowKey ? (
          <Typography
            component="span"
            sx={[eyebrow, theme => ({ marginBottom: theme.visualPrimitives.wizEyebrowGap })]}
          >
            {t(eyebrowKey)}
          </Typography>
        ) : null}
        {titleKey ? (
          <Typography
            component="h1"
            sx={theme => ({
              fontSize: theme.visualPrimitives.questionSize,
              fontWeight: theme.visualPrimitives.questionWeight,
              letterSpacing: theme.visualPrimitives.questionTracking,
              lineHeight: theme.visualPrimitives.questionLineHeight,
              color: theme.colors.inkPrimary,
              margin: 0,
            })}
          >
            {t(titleKey)}
          </Typography>
        ) : null}
        {hintKey ? (
          <Typography
            sx={theme => ({
              fontSize: theme.visualPrimitives.hintSize,
              maxWidth: theme.visualPrimitives.hintMaxWidth,
              lineHeight: theme.visualPrimitives.hintLineHeight,
              color: theme.colors.inkSecondary,
              marginTop: theme.visualPrimitives.hintGap,
            })}
          >
            {t(hintKey)}
          </Typography>
        ) : null}
        {children}
        {actions ? (
          <Box
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginTop: theme.visualPrimitives.wizNavGap,
              gap: theme.visualPrimitives.wizNavItemGap,
            })}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default AdvisoryEngagementShell;
